import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';

// Components
import Navbar from './components/Navbar';
import Home from './components/Home';
import Register from './components/Register';
import RegisterWithOTP from './components/RegisterWithOTP';
import Login from './components/Login';
import Rooms from './components/Rooms';
import AllRooms from './components/AllRooms';
import Cart from './components/Cart';
import AdminPanel from './components/AdminPanel';
import Gallery from './components/Gallery';
import Profile from './components/Profile';
import InactivityWarning from './components/InactivityWarning';
import ToastContainer from './components/Toast';
import Footer from './components/Footer';
import BookRooms from './components/BookRoomsSimple';
import ContactUs from './components/ContactUs';

// Hooks and utilities
import useInactivityLogout from './hooks/useInactivityLogout';
import { logoutUser, isTokenExpired } from './utils/auth';

// Inner component that uses navigation
function AppContent() {
  const [user, setUser] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [isBookRoomsOpen, setIsBookRoomsOpen] = useState(false);
  const [isContactUsOpen, setIsContactUsOpen] = useState(false);
  const navigate = useNavigate();

  // Logout function for inactivity
  const handleInactivityLogout = () => {
    logoutUser(setUser, clearCart, navigate, true);
  };

  // Set up inactivity logout (30 minutes total, 2 minutes warning)
  const { disableTimeout, showWarning, warningTimeRemaining, extendSession } = useInactivityLogout(
    handleInactivityLogout, 
    30 * 60 * 1000, // 30 minutes total
    2 * 60 * 1000   // 2 minutes warning
  );

  // Load user from localStorage on app start
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    console.log('=== APP STARTUP TOKEN CHECK ===');
    console.log('Saved user exists:', !!savedUser);
    console.log('Token exists:', !!token);
    
    if (savedUser && token) {
      const parsedUser = JSON.parse(savedUser);
      console.log('Parsed user:', parsedUser);
      console.log('User role:', parsedUser.role);
      
      // Check if token is expired - pass user object for admin detection
      const expired = isTokenExpired(token, parsedUser);
      console.log('Token expired check result:', expired);
      
      if (expired) {
        console.log('Token expired on app startup, logging out user');
        logoutUser(setUser, clearCart, navigate, false);
      } else {
        console.log('Token valid, setting user');
        setUser(parsedUser);
      }
    }

    // Load cart only for the current user
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      const userSpecificCart = localStorage.getItem(`cart_${parsedUser.id}`);
      
      if (userSpecificCart) {
        try {
          const parsedCart = JSON.parse(userSpecificCart);
          setCartItems(parsedCart.items || parsedCart);
        } catch (error) {
          console.error('Error parsing user cart:', error);
          setCartItems([]);
        }
      } else {
        setCartItems([]);
      }
      
      // Clean up old cart storage format
      localStorage.removeItem('cartItems');
    }
  }, []);

  // Disable inactivity timeout when user logs out manually
  useEffect(() => {
    if (!user) {
      disableTimeout();
    }
  }, [user, disableTimeout]);

  // Save cart to localStorage whenever it changes (with user isolation)
  useEffect(() => {
    if (user && cartItems.length > 0) {
      const cartData = {
        userId: user.id,
        items: cartItems,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem(`cart_${user.id}`, JSON.stringify(cartData));
    } else if (user && cartItems.length === 0) {
      localStorage.removeItem(`cart_${user.id}`);
    }
  }, [cartItems, user]);

  // Google Hotel Center: Auto-open booking modal when URL has booking parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasGoogleHotelParams = 
      urlParams.has('room') || 
      urlParams.has('date') || 
      urlParams.has('occupancy');

    if (hasGoogleHotelParams) {
      console.log('🏨 Google Hotel Center deep link detected - auto-opening booking modal');
      setIsBookRoomsOpen(true);
    }
  }, []); // Run only once on mount

  const addToCart = (booking) => {
    console.log('=== ADDING TO CART ===');
    console.log('Current cart items:', cartItems);
    console.log('New booking:', booking);
    const newCartItems = [...cartItems, booking];
    setCartItems(newCartItems);
    console.log('Updated cart items:', newCartItems);
  };

  const removeFromCart = (index) => {
    const updatedCart = cartItems.filter((_, i) => i !== index);
    setCartItems(updatedCart);
  };

  const clearCart = () => {
    setCartItems([]);
    if (user) {
      localStorage.removeItem(`cart_${user.id}`);
    }
  };

  const handleBookRoomsClick = () => {
    console.log('BookRooms button clicked!');
    console.log('Current isBookRoomsOpen:', isBookRoomsOpen);
    setIsBookRoomsOpen(true);
    console.log('Setting isBookRoomsOpen to true');
  };

  const handleBookRoomsClose = () => {
    setIsBookRoomsOpen(false);
  };

  const handleContactUsClick = () => {
    setIsContactUsOpen(true);
  };

  const handleContactUsClose = () => {
    setIsContactUsOpen(false);
  };

  return (
    <div className="App">
      <Navbar 
        user={user} 
        setUser={setUser} 
        cartItems={user?.role === 'admin' ? [] : cartItems} 
        onBookRoomsClick={handleBookRoomsClick}
        onContactUsClick={handleContactUsClick}
      />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Home onBookRoomsClick={handleBookRoomsClick} />} />
          {/* <Route path="/register" element={<RegisterWithOTP setUser={setUser} />} /> */}
          {/* <Route path="/register-old" element={<Register setUser={setUser} />} /> */}
          <Route path="/login" element={<Login setUser={setUser} />} />
          {/* <Route path="/rooms" element={<Rooms user={user} addToCart={addToCart} setUser={setUser} />} /> */}
          <Route path="/rooms" element={<AllRooms user={user} />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/cart" element={<Cart cartItems={user?.role === 'admin' ? [] : cartItems} removeFromCart={removeFromCart} clearCart={clearCart} user={user} />} />
          <Route path="/profile" element={<Profile user={user} />} />
          <Route path="/admin" element={<AdminPanel user={user} />} />
        </Routes>
      </main>
      
      {/* Footer - but not on admin page */}
      <Footer />
      
      {/* Book Rooms Modal */}
      {isBookRoomsOpen && (
        <BookRooms 
          onClose={handleBookRoomsClose}
          user={user}
        />
      )}
      
      {/* Contact Us Modal */}
      {isContactUsOpen && (
        <ContactUs 
          isOpen={isContactUsOpen}
          onClose={handleContactUsClose}
        />
      )}
      
      {/* Inactivity Warning Modal */}
      {user && (
        <InactivityWarning
          isVisible={showWarning}
          timeRemaining={warningTimeRemaining}
          onExtendSession={extendSession}
          onLogout={() => logoutUser(setUser, clearCart, navigate, false)}
        />
      )}
      
      {/* Toast Container */}
      <ToastContainer />
    </div>
  );
}

// Main App component with Router
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
